/**
 * useAccount Hook
 *
 * Provides account abstraction with terminology helpers for the UI.
 * Wraps the useAgency hook to add account-specific logic.
 *
 * Terminology mapping:
 * - Personal accounts (free/pro users): "Account" in UI
 * - Team accounts (agency plan): "Agency" in UI for agency tier, "Account" otherwise
 * - System accounts: Internal only, not shown in UI
 */

import { useAuth } from '@/contexts/AuthContext'
import { useAgency } from './useAgency'
import { Agency, AccountType } from '@/types'
import { supabase } from '@/lib/supabase'
import { useCallback, useMemo } from 'react'

export interface AccountInfo {
  /** The underlying agency record */
  account: Agency | null
  /** Whether this is a personal (single-user) account */
  isPersonalAccount: boolean
  /** Whether this is a team (multi-user) account */
  isTeamAccount: boolean
  /** Whether this is the system account (Framed's token pool) */
  isSystemAccount: boolean
  /** Display name for UI: "Account" for personal/pro, "Agency" for team with agency plan */
  displayName: string
  /** Whether the current user is the owner of this account */
  isOwner: boolean
  /** Whether team features (invite members, etc.) are available */
  canInviteMembers: boolean
  /** Whether the account can be converted to a team account */
  canConvertToTeam: boolean
  /** Loading state */
  loading: boolean
  /** Error if any */
  error: string | null
  /** Refresh account data */
  refresh: () => void
  /** Convert personal account to team account */
  convertToTeamAccount: () => Promise<{ success: boolean; error?: string }>
}

export function useAccount(): AccountInfo {
  const { user } = useAuth()
  const agencyId = user?.agency_id
  const { agency, loading, error } = useAgency(agencyId)

  const accountType: AccountType | undefined = agency?.account_type

  // Determine account characteristics
  const isPersonalAccount = accountType === 'personal'
  const isTeamAccount = accountType === 'team'
  const isSystemAccount = accountType === 'system'

  // Check if current user is the owner
  const isOwner = useMemo(() => {
    if (!user || !agency) return false
    // For personal accounts, the owner_user_id should match
    if (agency.owner_user_id) {
      return agency.owner_user_id === user.id
    }
    // For team accounts created before this migration, check if user is admin
    return user.role === 'admin' || user.role === 'super_admin'
  }, [user, agency])

  // Determine display name based on account type and plan
  const displayName = useMemo(() => {
    if (!agency) return 'Account'

    // Team accounts with agency plan are called "Agency"
    if (isTeamAccount && agency.plan_type === 'agency') {
      return 'Agency'
    }

    // Everything else is called "Account"
    return 'Account'
  }, [agency, isTeamAccount])

  // Team features are only available for team accounts
  const canInviteMembers = isTeamAccount && isOwner

  // Personal accounts can be converted to team accounts
  const canConvertToTeam = isPersonalAccount && isOwner

  // Refresh function (re-fetch agency data)
  const refresh = useCallback(() => {
    // The useAgency hook doesn't expose a refresh function,
    // but we can trigger a re-fetch by the dependency array
    // For now, this is a placeholder - we may need to enhance useAgency
  }, [])

  // Convert to team account
  const convertToTeamAccount = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!agencyId) {
      return { success: false, error: 'No account found' }
    }

    if (!isPersonalAccount) {
      return { success: false, error: 'Account is already a team account' }
    }

    if (!isOwner) {
      return { success: false, error: 'Only the account owner can convert to a team account' }
    }

    try {
      // Call the database function to convert
      const { error } = await supabase.rpc('convert_to_team_account', {
        p_agency_id: agencyId
      })

      if (error) {
        console.error('Error converting to team account:', error)
        return { success: false, error: error.message }
      }

      // Trigger a page refresh to update the UI
      // In a real implementation, we'd update the local state
      window.location.reload()

      return { success: true }
    } catch (err) {
      console.error('Error converting to team account:', err)
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to convert account'
      }
    }
  }, [agencyId, isPersonalAccount, isOwner])

  return {
    account: agency,
    isPersonalAccount,
    isTeamAccount,
    isSystemAccount,
    displayName,
    isOwner,
    canInviteMembers,
    canConvertToTeam,
    loading,
    error,
    refresh,
    convertToTeamAccount,
  }
}

/**
 * Hook to get just the account display name without loading full account data.
 * Useful for UI components that only need the terminology.
 */
export function useAccountDisplayName(): string {
  const { account, displayName } = useAccount()
  return account ? displayName : 'Account'
}

/**
 * Hook to check if the current user has a personal account.
 */
export function useIsPersonalAccount(): boolean {
  const { isPersonalAccount } = useAccount()
  return isPersonalAccount
}
